import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  GiftIcon,
  StarIcon,
  ChartBarIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  ShoppingBagIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { CalendarDate, today, getLocalTimeZone } from "@internationalized/date";
import { RangeValue } from "@react-types/shared";
import { Timestamp } from "firebase/firestore";

// Local imports
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import StyledMessage from "../../components/StyledMessage";
import { ExistingLoyaltyProgramTemplate } from "../../models/loyalty-program-template";
import {
  ExistingLoyaltyTransaction,
  TransactionStatus,
} from "../../models/loyalty-transaction";
import { DbRead } from "../../database";
import { useToastMessageStore } from "../../store/toast-messages";
import { createToastMessageID } from "../../utils";
import * as urls from "../../urls";
import { MoreRewardsOffer } from "../../database/more-rewards";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { useSiteKeyLocationsStore } from "../../store/site-key-locations";

interface Props {
  siteKey: string;
}

export default function LoyaltyDashboardContainer({ siteKey }: Props) {
  const navigate = useNavigate();
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );
  const siteKeyDoc = useSiteKeyDocStore((state) => state.siteKeyDoc);
  const siteKeyLocations = useSiteKeyLocationsStore(
    (state) => state.siteKeyLocationList,
  );

  const [programs, setPrograms] = useState<ExistingLoyaltyProgramTemplate[]>(
    [],
  );
  const [pendingTransactions, setPendingTransactions] = useState<
    ExistingLoyaltyTransaction[]
  >([]);
  const [recentTransactions, setRecentTransactions] = useState<
    ExistingLoyaltyTransaction[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<
    "overview" | "programs" | "transactions"
  >("overview");
  const [offers, setOffers] = useState<Record<string, MoreRewardsOffer[]>>({});
  const [offersLoading, setOffersLoading] = useState(false);
  const [moreRewardsConfig, setMoreRewardsConfig] = useState<any>(null);
  const [isMoreRewardsExpanded, setIsMoreRewardsExpanded] = useState(false);

  // Date range state - default to last 3 months for recent transactions
  const getDefaultDateRange = (): RangeValue<CalendarDate> => {
    return {
      start: today(getLocalTimeZone()).subtract({ months: 3 }),
      end: today(getLocalTimeZone()),
    };
  };

  const [range] = useState<RangeValue<CalendarDate>>(getDefaultDateRange());

  // Convert CalendarDate to Firestore Timestamp
  const calendarDateToTimestamp = (calendarDate: CalendarDate): Timestamp => {
    const date = new Date(
      calendarDate.year,
      calendarDate.month - 1,
      calendarDate.day,
    );
    return Timestamp.fromDate(date);
  };

  useEffect(() => {
    // Subscribe to loyalty programs
    const unsubscribePrograms = DbRead.loyaltyPrograms.subscribe({
      siteKey,
      onChange: (programs) => {
        setPrograms(programs);
      },
      onError: (error) => {
        console.error("Error fetching loyalty programs:", error);
        addToastMessage({
          id: createToastMessageID(),
          message: "Error fetching loyalty programs",
          type: "error",
          dialog: false,
        });
      },
    });

    // Subscribe to pending transactions
    const unsubscribePending = DbRead.loyaltyTransactions.subscribePending({
      siteKey,
      onChange: (transactions) => {
        setPendingTransactions(transactions);
      },
      onError: (error) => {
        console.error("Error fetching pending transactions:", error);
        addToastMessage({
          id: createToastMessageID(),
          message: "Error fetching pending transactions",
          type: "error",
          dialog: false,
        });
      },
    });

    // Subscribe to transactions with date range for recent view
    const startTimestamp = calendarDateToTimestamp(range.start);
    const endTimestamp = calendarDateToTimestamp(range.end);

    const unsubscribeTransactions =
      DbRead.loyaltyTransactions.subscribeWithDateRange({
        siteKey,
        startDate: startTimestamp,
        endDate: endTimestamp,
        onChange: (transactions) => {
          setRecentTransactions(transactions.slice(0, 5)); // Show only recent 5
          setLoading(false);
        },
        onError: (error) => {
          console.error("Error fetching loyalty transactions:", error);
          addToastMessage({
            id: createToastMessageID(),
            message: "Error fetching loyalty transactions",
            type: "error",
            dialog: false,
          });
          setLoading(false);
        },
      });

    return () => {
      unsubscribePrograms();
      unsubscribePending();
      unsubscribeTransactions();
    };
  }, [siteKey, addToastMessage, range]);

  // Fetch MoreRewards offers
  useEffect(() => {
    async function fetchMoreRewardsOffers() {
      if (!siteKeyDoc?.customizations?.moreRewards) {
        setMoreRewardsConfig(null);
        return;
      }

      const config = siteKeyDoc.customizations.moreRewards;
      setMoreRewardsConfig(config);
      setOffersLoading(true);

      const newOffers: Record<string, MoreRewardsOffer[]> = {};

      // Fetch offers for each siteKeyLocation
      for (const location of siteKeyLocations) {
        try {
          const response = await DbRead.moreRewards.getOffers(
            siteKey,
            location.id,
          );
          if (response.success) {
            newOffers[location.id] = response.offers;
          }
        } catch (error) {
          console.error(
            `Error fetching offers for location ${location.id}:`,
            error,
          );
        }
      }

      setOffers(newOffers);
      setOffersLoading(false);
    }

    fetchMoreRewardsOffers();
  }, [siteKey, siteKeyDoc]);

  // Navigation handlers
  const handleCreateProgram = () => {
    navigate(urls.LOYALTY_PROGRAMS_CREATE_URL);
  };

  const handleViewPrograms = () => {
    navigate(urls.LOYALTY_PROGRAMS_URL);
  };

  const handleViewTransactions = () => {
    navigate(urls.LOYALTY_TRANSACTIONS_URL);
  };

  const handleEditProgram = (programId: string) => {
    navigate(`${urls.LOYALTY_PROGRAMS_EDIT_URL}/${programId}`);
  };

  // Calculate MoreRewards status
  const getMoreRewardsStatus = () => {
    if (offersLoading) {
      return "Loading...";
    }

    const locationsWithOffers = Object.keys(offers).filter(
      (locationId) => offers[locationId].length > 0,
    ).length;

    const totalLocations = siteKeyLocations.length;

    if (totalLocations === 0) {
      return "No locations";
    }

    return `${locationsWithOffers}/${totalLocations} locations configured`;
  };

  const getLocationName = (locationId: string): string => {
    const location = siteKeyLocations.find((loc) => loc.id === locationId);
    return location?.title || locationId;
  };

  const stats = {
    totalPrograms: programs.length,
    activePrograms: programs.filter((p) => p.isActive).length,
    pendingTransactions: pendingTransactions.length,
    totalTransactions: recentTransactions.length,
  };

  if (loading && programs.length === 0) {
    return (
      <div className="flex h-full flex-auto flex-col items-center justify-center">
        <LoadingClipboardAnimation />
        <p className="mt-4 text-gray-600">Loading loyalty dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Loyalty & Rewards Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your customer loyalty programs and track reward transactions
          </p>
        </div>
        <div className="flex gap-3">
          <BaseButtonSecondary onClick={handleViewPrograms}>
            <EyeIcon className="h-4 w-4" />
            View All Programs
          </BaseButtonSecondary>
          <BaseButtonPrimary onClick={handleCreateProgram}>
            <PlusIcon className="h-4 w-4" />
            Create Program
          </BaseButtonPrimary>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <GiftIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="truncate text-sm font-medium text-gray-500">
                  Total Programs
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats.totalPrograms}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <StarIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="truncate text-sm font-medium text-gray-500">
                  Active Programs
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats.activePrograms}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="truncate text-sm font-medium text-gray-500">
                  Pending Transactions
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats.pendingTransactions}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChartBarIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="truncate text-sm font-medium text-gray-500">
                  Recent Transactions
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats.totalTransactions}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* MoreRewards Integration Status */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <button
          onClick={() => setIsMoreRewardsExpanded(!isMoreRewardsExpanded)}
          className="w-full p-6 text-left transition-colors hover:bg-gray-50"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/moreRewards_logo.svg"
                alt="MoreRewards"
                className="h-6"
              />
              <h3 className="text-lg font-medium text-gray-900">Integration</h3>
              {isMoreRewardsExpanded ? (
                <ChevronDownIcon className="ml-2 h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="ml-2 h-5 w-5 text-gray-400" />
              )}
            </div>
            <div className="text-sm text-gray-600">
              {!moreRewardsConfig ? (
                <span className="text-yellow-600">Not configured</span>
              ) : (
                getMoreRewardsStatus()
              )}
            </div>
          </div>
        </button>

        {isMoreRewardsExpanded && (
          <div className="border-t border-gray-200 p-6">
            {!moreRewardsConfig ? (
              <div className="flex items-center rounded-md border border-yellow-200 bg-yellow-50 p-4">
                <ExclamationTriangleIcon className="mr-3 h-5 w-5 flex-shrink-0 text-yellow-600" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    MoreRewards Not Configured
                  </p>
                  <p className="text-sm text-yellow-700">
                    Configure MoreRewards in your site customizations to enable
                    reward offers integration.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(offers).map(
                    ([locationID, locationOffers]) => (
                      <div
                        key={locationID}
                        className="rounded-lg border border-gray-200 p-4"
                      >
                        <div className="mb-3 flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">
                            {getLocationName(locationID)}
                          </h4>
                          <ShoppingBagIcon className="h-4 w-4 text-blue-500" />
                        </div>

                        {offersLoading ? (
                          <div className="text-sm text-gray-500">
                            Loading offers...
                          </div>
                        ) : locationOffers.length > 0 ? (
                          <div>
                            <p className="mb-2 text-sm font-medium text-green-600">
                              {locationOffers.length} offers available
                            </p>
                            <div className="max-h-32 space-y-2 overflow-y-auto">
                              {locationOffers.slice(0, 3).map((offer) => (
                                <div
                                  key={offer.offerId}
                                  className="rounded border bg-gray-50 p-2 text-xs"
                                >
                                  <div className="font-medium text-gray-900">
                                    {offer.offerName}
                                  </div>
                                  <div className="text-gray-600">
                                    Description: {offer.offerDescription}
                                  </div>
                                </div>
                              ))}
                              {locationOffers.length > 3 && (
                                <div className="text-xs italic text-gray-500">
                                  +{locationOffers.length - 3} more offers
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500">
                            No offers available
                          </div>
                        )}
                      </div>
                    ),
                  )}
                </div>

                {offersLoading && Object.keys(offers).length === 0 && (
                  <div className="py-4 text-center">
                    <div className="text-sm text-gray-500">
                      Loading offers from MoreRewards...
                    </div>
                  </div>
                )}

                {siteKeyLocations.length === 0 && (
                  <div className="py-4 text-center">
                    <div className="text-sm text-gray-500">
                      No site locations configured
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="mb-4 text-lg font-medium text-gray-900">
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <BaseButtonSecondary
            onClick={handleCreateProgram}
            className="justify-center"
          >
            <PlusIcon className="mr-2 h-5 w-5" />
            Create New Program
          </BaseButtonSecondary>
          <BaseButtonSecondary
            onClick={handleViewPrograms}
            className="justify-center"
          >
            <GiftIcon className="mr-2 h-5 w-5" />
            Manage Programs
          </BaseButtonSecondary>
          <BaseButtonSecondary
            onClick={handleViewTransactions}
            className="justify-center"
          >
            <CheckCircleIcon className="mr-2 h-5 w-5" />
            Review Transactions
          </BaseButtonSecondary>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: "overview", label: "Overview", icon: ChartBarIcon },
            { key: "programs", label: "Recent Programs", icon: GiftIcon },
            {
              key: "transactions",
              label: "Pending Transactions",
              icon: ClockIcon,
            },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setSelectedTab(key as typeof selectedTab)}
              className={`flex items-center border-b-2 px-1 py-2 text-sm font-medium ${
                selectedTab === key
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <Icon className="mr-2 h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {selectedTab === "overview" && (
          <div className="space-y-6">
            {stats.pendingTransactions > 0 && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-6">
                <div className="flex items-center">
                  <ClockIcon className="h-6 w-6 text-yellow-600" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Pending Transactions Require Attention
                    </h3>
                    <p className="mt-1 text-sm text-yellow-700">
                      You have {stats.pendingTransactions} loyalty transactions
                      waiting for approval.
                    </p>
                  </div>
                  <div className="ml-auto">
                    <BaseButtonSecondary onClick={handleViewTransactions}>
                      Review Now
                    </BaseButtonSecondary>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {selectedTab === "programs" && (
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-medium text-gray-900">
                Recent Loyalty Programs
              </h3>
            </div>
            <div className="overflow-hidden">
              {programs.length === 0 ? (
                <div className="py-8 text-center">
                  <GiftIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No loyalty programs
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by creating your first loyalty program.
                  </p>
                  <div className="mt-6">
                    <BaseButtonPrimary onClick={handleCreateProgram}>
                      <PlusIcon className="mr-2 h-4 w-4" />
                      Create Program
                    </BaseButtonPrimary>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {programs.slice(0, 5).map((program) => (
                    <div key={program.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center">
                            <h4 className="truncate text-sm font-medium text-gray-900">
                              {program.name}
                            </h4>
                            <span
                              className={`ml-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                program.isActive
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {program.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center text-sm text-gray-500">
                            <span>{program.programType}</span>
                            <span className="mx-2">•</span>
                            <span>{program.triggerEvent}</span>
                            <span className="mx-2">•</span>
                            <span>{program.rewardType}</span>
                          </div>
                          <p className="mt-1 truncate text-sm text-gray-500">
                            {program.description}
                          </p>
                        </div>
                        <div className="ml-4 flex items-center space-x-2">
                          <BaseButtonSecondary
                            onClick={() => handleEditProgram(program.id)}
                            className="text-sm"
                          >
                            Edit
                          </BaseButtonSecondary>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {programs.length > 5 && (
              <div className="border-t border-gray-200 px-6 py-3 text-center">
                <BaseButtonSecondary onClick={handleViewPrograms}>
                  View All Programs ({programs.length})
                </BaseButtonSecondary>
              </div>
            )}
          </div>
        )}

        {selectedTab === "transactions" && (
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-medium text-gray-900">
                Pending Transactions
              </h3>
            </div>
            <div className="overflow-hidden">
              {pendingTransactions.length === 0 ? (
                <div className="py-8 text-center">
                  <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No pending transactions
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    All loyalty transactions have been processed.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {pendingTransactions.slice(0, 5).map((transaction) => (
                    <div key={transaction.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center">
                            <h4 className="text-sm font-medium text-gray-900">
                              Customer: {transaction.customerID}
                            </h4>
                            <span
                              className={`ml-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                transaction.status === TransactionStatus.PENDING
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {transaction.status}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center text-sm text-gray-500">
                            <span>{transaction.rewardType}</span>
                            <span className="mx-2">•</span>
                            <span>
                              Amount: ${transaction.rewardValue.toFixed(2)}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Created:{" "}
                            {new Date(
                              transaction.timestampCreated.toDate(),
                            ).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="ml-4 flex items-center space-x-2">
                          <BaseButtonSecondary className="text-sm">
                            Review
                          </BaseButtonSecondary>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {pendingTransactions.length > 5 && (
              <div className="border-t border-gray-200 px-6 py-3 text-center">
                <BaseButtonSecondary onClick={handleViewTransactions}>
                  View All Transactions ({pendingTransactions.length})
                </BaseButtonSecondary>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
